<script lang="ts">
	import { SnapshotSelector } from "@calibration/snapshot-modifier"
	import SnapshotModifier from "./SnapshotModifier.svelte"
	import SelectDevice from "./DeviceSelector.svelte"
	import SnapshotSelectStep from "./SnapshotSelect_Step.svelte"
	import { getLuxAiController } from "../LuxAIController.svelte"
	import { Responsive } from "responsive"
	import { closeOverlay, openOverlay } from "overlay"
	import type { DeviceData, SnapshotData } from "../../types"

	type StepName = "device" | "snapshot"

	const luxAiController = getLuxAiController()
	let selectedTheme = $derived(luxAiController.selectedTheme)
	let isFree = $derived(selectedTheme.tags.includes("free"))

	let tokenCost = $derived(isFree ? 0 : 10)

	let userTokens = luxAiController.userDataGetter.getCurrentUserData().availableTokens
	let currentStep: StepName = $state("device")

	let availableDevices: DeviceData[] = $state<DeviceData[]>(
		luxAiController.deviceDataGetter.listCompatibleDevices(),
	)

	let deviceAmount = $derived(availableDevices.length)

	let selectedDevice = $state<DeviceData>()

	let allDeviceSnapshots: SnapshotData[] = $derived(selectedDevice.snapshots)
	let availableSnapshots: SnapshotData[] = $derived(
		allDeviceSnapshots?.filter((s) => s.edited) ?? [],
	)

	let hasSnapshots = $derived(allDeviceSnapshots.length >= 1)
	let hasAvailableSnapshots = $derived(availableSnapshots.length >= 1)

	let selectedSnapshot = $state<SnapshotData>()

	function selectDevice(device: DeviceData) {
		selectedDevice = device
	}

	function verifyDevice() {
		currentStep = "snapshot"
	}

	function selectSnapshot(snapshotId: string) {
		selectedSnapshot = luxAiController.deviceDataGetter.getSnapshotById(snapshotId)
	}

	function verifySnapshot() {
		luxAiController.selectSnapshot(selectedSnapshot)
	}

	function modifyExisting() {
		const overlayId = openOverlay(SnapshotModifier, {
			classOverlay: "no-pad can-grow full-sized",
			hideExitButton: true,
			props: {
				snapshots: allDeviceSnapshots.filter((s) => !s.edited),
				onComplete: async (snap) => {
					await luxAiController.deviceDataGetter.refreshSnapshots(selectedDevice.id)
					refreshSnapshots()
					selectSnapshot(snap.id.toString())
					closeOverlay(overlayId)
				},
				cancel: () => closeOverlay(overlayId),
			},
		})
	}

	function refreshSnapshots() {
		let device = luxAiController.deviceDataGetter.getDevice(selectedDevice.id)
		selectDevice(device)
	}

	function goBack() {
		luxAiController.view = "gallery"
	}

	$effect(() => {
		if (deviceAmount === 1) {
			selectDevice(availableDevices[0])
			currentStep = "snapshot"
		}
	})

	$effect(() => {
		if (selectedDevice) {
			// refreshSnapshots()
		}
	})
</script>

{#snippet snapshotSelection()}
	<div class="select-snapshot">
		<p class="info basic-info">
			In order to generate AI content, you need to use a snapshot which has had its background
			removed.
			<br />
			{#if hasSnapshots}
				<span class="flex-row">
					<button class="outline-button square-button" onclick={modifyExisting}
						>Modify an Existing Snapshot</button
					>
					{#if hasAvailableSnapshots}
						<span class="info"> or use an already modified snapshot below.</span>
					{/if}
				</span>
			{/if}
		</p>
		{#if !hasAvailableSnapshots}
			<p class="warn basic-info">
				{#if !hasSnapshots}
					<span>
						It looks like you haven't calibrated your device yet. <button
							class="outline-button alt small"
							onclick={() => luxAiController.quickActions.calibrateDevice(selectedDevice.id)}
							>Calibrate your device</button
						> to get started.
					</span>
				{:else}
					<span>
						It looks like you haven't created any yet. Click the Modify Snapshot button to get
						started.
					</span>
				{/if}
			</p>
		{/if}
		{#if hasAvailableSnapshots}
			<SnapshotSelector
				snapshotIds={availableSnapshots.map((snap) => Number(snap.id))}
				onselect={(s) => selectSnapshot(s.id.toString())}
				selectedSnapshotId={Number(selectedSnapshot?.id)}
			/>
		{/if}
	</div>
	<div class="token-verify">
		<span class="token-preview"
			>{userTokens} - {tokenCost} = {userTokens - tokenCost} tokens remain</span
		>
		<div class="button-container">
			<button class="outline-button warn" onclick={() => (currentStep = "device")}>Go Back</button>

			{#if hasAvailableSnapshots}
				<button class="outline-button" disabled={!selectedSnapshot} onclick={verifySnapshot}
					>Ok</button
				>
			{/if}
		</div>
	</div>
{/snippet}

<div class="snapshot-select-container" class:mobile={Responsive.isMobile}>
	<div class="heading">
		<h1>Generating {selectedTheme.name}</h1>
		<p class="info">
			Choose your Luxedo device and a prepared snapshot. The snapshot must have its background
			removed so the AI can use your space as the foundation for generating themed content. If you
			haven't prepared a snapshot, don't worry - you will be able to in step #2.
		</p>
	</div>

	{#if deviceAmount}
		<SnapshotSelectStep
			stepNumber={1}
			title={"Select your device"}
			description="Select the device to generate content"
			isActive={currentStep === "device"}
		>
			{#snippet content()}
				<SelectDevice onSelect={selectDevice} {availableDevices} {selectedDevice} />
				<div class="button-container">
					<button class="outline-button warn" onclick={goBack}>Go Back</button>
					<button class="outline-button" disabled={!selectedDevice} onclick={verifyDevice}
						>Next</button
					>
				</div>
			{/snippet}
		</SnapshotSelectStep>
		<SnapshotSelectStep
			stepNumber={2}
			title={"Select your snapshot"}
			description="Select the snapshot to use as the backdrop for content creation. "
			isActive={currentStep === "snapshot"}
		>
			{#snippet content()}
				{@render snapshotSelection()}
			{/snippet}
		</SnapshotSelectStep>
	{:else}
		<SnapshotSelectStep
			stepNumber={1}
			title={"Select your device"}
			description="Select the device to generate content"
			isActive={currentStep === "device"}
		>
			{#snippet content()}
				<p class="info basic-info">
					Looks like you haven't registered a device yet! <br /><button
						class="outline-button square-button alt small"
						onclick={() => luxAiController.quickActions.jumpToRegister()}>Register a device</button
					> now to get started.
				</p>

				<div class="button-container">
					<button class="outline-button warn" onclick={goBack}>Go Back</button>
				</div>
			{/snippet}
		</SnapshotSelectStep>
	{/if}
</div>

<style>
	h1 {
		width: 100%;
		margin-bottom: 0;
	}

	.heading .info {
		color: var(--color-text);
	}

	.snapshot-select-container {
		display: flex;
		flex-direction: column;
		align-items: center;
		height: 100%;
		overflow: hidden;
	}

	.button-container {
		padding: 1rem 0 0 0;
		margin: auto 0 0 0;
		justify-content: flex-end;
	}

	.token-preview {
		color: var(--color-text);
	}

	.mobile h1 {
		font-size: var(--h1);
		margin-top: 1rem;
	}

	.mobile .info {
		font-size: var(--h3);
	}

	.mobile :global(.step:not(.active)) {
		display: none;
	}

	.info {
		margin-bottom: 0;
	}

	.basic-info {
		margin-bottom: 0;
		line-height: 1.4em;
	}

	.select-snapshot {
		display: flex;
		flex-direction: column;
		flex: 1;
		overflow-y: hidden;
	}

	p.warn {
		color: var(--color-error);
	}

	.flex-row {
		margin-bottom: 1rem;
		margin-top: 0.4rem;
		align-items: center;
		gap: 0.5rem;
	}

	.mobile .flex-row {
		gap: 0.25rem;
		margin-top: 0.5rem;
	}

	.square-button {
		font-size: var(--h3);
		padding: 0 0.5rem;
	}

	.token-verify {
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		margin-top: 0.5rem;
	}

	.token-verify .button-container {
		padding-top: 0.25rem;
	}
</style>
